import React, { useEffect, useState, memo } from 'react';
import { Timer, CheckCircle2, AlertTriangle, Trophy } from 'lucide-react';
export function PhaseChallenge({
  phase,
  team,
  onComplete
}) {
  const [challenge, setChallenge] = useState(null);
  const [teamSolution, setTeamSolution] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [status, setStatus] = useState('preparing'); // 'preparing', 'in-progress', 'completed', 'evaluating', 'success', 'failed'
  const [evaluation, setEvaluation] = useState(null);
  useEffect(() => {
    // Set up the challenge based on the phase
    if (phase.id === 3) {
      // Problem Validation
      setChallenge({
        title: 'Problem Validation Challenge',
        description: "Your team needs to validate that the problem you've identified is real and worth solving.",
        task: 'Create a set of 5 interview questions you would ask potential customers to validate your problem hypothesis.',
        timeLimit: 600,
        criteria: ['Questions should be open-ended, not yes/no', 'Questions should avoid leading the interviewee', 'Questions should help uncover the severity of the problem', 'Questions should help identify if people would pay for a solution', 'Questions should help understand current alternatives people use']
      });
    } else if (phase.id === 6) {
      // Pitch Development
      setChallenge({
        title: 'Pitch Development Challenge',
        description: 'Your team needs to create a compelling elevator pitch for your startup.',
        task: "Create a 30-second elevator pitch that clearly communicates your startup's value proposition.",
        timeLimit: 900,
        criteria: ["Clearly identifies the problem you're solving", 'Explains your unique solution', 'Identifies your target audience', 'Communicates your unique value proposition', 'Is memorable and concise (under 100 words)']
      });
    }
  }, [phase.id]);
  useEffect(() => {
    // Start the timer when challenge is set and status changes to in-progress
    if (challenge && status === 'in-progress') {
      setTimeRemaining(challenge.timeLimit);
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setStatus('evaluating');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [challenge, status]);
  const startChallenge = () => {
    setStatus('in-progress');
  };
  const submitSolution = () => {
    setStatus('evaluating');
    // Simulate AI evaluation of the solution
    setTimeout(() => {
      // Calculate a score based on criteria met
      const criteriaResults = challenge.criteria.map(criterion => {
        const randomScore = Math.random();
        return {
          criterion,
          met: randomScore > 0.3,
          feedback: randomScore > 0.3 ? 'Well done! This criterion was met successfully.' : 'This could be improved by being more specific and focused.'
        };
      });
      const metCount = criteriaResults.filter(cr => cr.met).length;
      const score = Math.round(metCount / challenge.criteria.length * 100);
      setEvaluation({
        score,
        criteriaResults,
        overallFeedback: score >= 70 ? 'Great work! Your solution meets most of the criteria and shows strong potential.' : 'Your solution has some good elements, but could be strengthened in several areas.'
      });
      setStatus(score >= 70 ? 'success' : 'failed');
    }, 2000);
  };
  const completeChallenge = () => {
    // Generate a summary of the challenge results
    const summary = `
      ## ${phase.name} Summary
      ### Challenge: ${challenge.title}
      **Team Solution:**
      ${teamSolution}
      **Evaluation Score:** ${evaluation.score}%
      **Criteria Assessment:**
      ${evaluation.criteriaResults.map(cr => `- ${cr.criterion}: ${cr.met ? '✅ Met' : '❌ Not Met'}`).join('\n')}
      **Overall Feedback:**
      ${evaluation.overallFeedback}
      **Next Steps:**
      ${evaluation.score >= 70 ? 'The team can proceed with confidence, incorporating the minor improvement suggestions.' : 'The team should revise their approach based on the feedback before proceeding.'}
    `;
    onComplete(summary);
  };
  if (!challenge) {
    return <div data-id="element-170">Loading challenge...</div>;
  }
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  return <div data-id="element-171">
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6" data-id="element-172">
        <h3 className="text-xl font-bold text-indigo-800 mb-3" data-id="element-173">
          {challenge.title}
        </h3>
        <p className="text-gray-700 mb-4" data-id="element-174">{challenge.description}</p>
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4" data-id="element-175">
          <h4 className="font-medium text-indigo-800 mb-2" data-id="element-176">Your Task:</h4>
          <p className="text-indigo-900" data-id="element-177">{challenge.task}</p>
        </div>
        <div className="mb-4" data-id="element-178">
          <h4 className="font-medium text-gray-800 mb-2" data-id="element-179">Success Criteria:</h4>
          <ul className="space-y-1" data-id="element-180">
            {challenge.criteria.map((criterion, index) => <li key={index} className="flex items-start" data-id="element-181">
                <span className="text-indigo-600 mr-2" data-id="element-182">•</span>
                <span className="text-gray-700" data-id="element-183">{criterion}</span>
              </li>)}
          </ul>
        </div>
        {status === 'preparing' && <div className="flex justify-center mt-6" data-id="element-184">
            <button className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition" onClick={startChallenge} data-id="element-185">
              Start Challenge
            </button>
          </div>}
        {status === 'in-progress' && <div className="mt-4" data-id="element-186">
            <div className="flex justify-between items-center mb-3" data-id="element-187">
              <h4 className="font-medium text-gray-800" data-id="element-188">
                Your Team's Solution:
              </h4>
              <div className="flex items-center text-amber-600" data-id="element-189">
                <Timer className="w-4 h-4 mr-1" data-id="element-190" />
                <span className="font-medium" data-id="element-191">{formatTime(timeRemaining)}</span>
              </div>
            </div>
            <textarea className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none" rows={8} placeholder="Type your team's solution here..." value={teamSolution} onChange={e => setTeamSolution(e.target.value)} data-id="element-192"></textarea>
            <div className="flex justify-end mt-4" data-id="element-193">
              <button className={`px-4 py-2 rounded-md ${teamSolution.length >= 50 ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} disabled={teamSolution.length < 50} onClick={submitSolution} data-id="element-194">
                Submit Solution
              </button>
            </div>
          </div>}
        {status === 'evaluating' && <div className="flex flex-col items-center justify-center py-8" data-id="element-195">
            <div className="w-12 h-12 border-t-2 border-b-2 border-indigo-600 rounded-full animate-spin mb-4" data-id="element-196"></div>
            <p className="text-indigo-800 font-medium" data-id="element-197">
              Evaluating your solution...
            </p>
          </div>}
        {(status === 'success' || status === 'failed') && evaluation && <div className="mt-6" data-id="element-198">
            <div className="flex items-center mb-4" data-id="element-199">
              {status === 'success' ? <div className="flex items-center text-green-600" data-id="element-200">
                  <CheckCircle2 className="w-6 h-6 mr-2" data-id="element-201" />
                  <h3 className="text-lg font-medium" data-id="element-202">
                    Challenge Completed Successfully!
                  </h3>
                </div> : <div className="flex items-center text-amber-600" data-id="element-203">
                  <AlertTriangle className="w-6 h-6 mr-2" data-id="element-204" />
                  <h3 className="text-lg font-medium" data-id="element-205">
                    Challenge Needs Improvement
                  </h3>
                </div>}
            </div>
            <div className="mb-4" data-id="element-206">
              <div className="flex justify-between items-center mb-2" data-id="element-207">
                <h4 className="font-medium" data-id="element-208">Score:</h4>
                <div className="flex items-center" data-id="element-209">
                  <Trophy className="w-4 h-4 mr-1 text-indigo-600" data-id="element-210" />
                  <span className={`font-bold ${evaluation.score >= 70 ? 'text-green-600' : 'text-amber-600'}`} data-id="element-211">
                    {evaluation.score}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-212">
                <div className={`h-2.5 rounded-full ${evaluation.score >= 70 ? 'bg-green-600' : 'bg-amber-500'}`} style={{
              width: `${evaluation.score}%`
            }} data-id="element-213"></div>
              </div>
            </div>
            <div className="mb-4" data-id="element-214">
              <h4 className="font-medium mb-2" data-id="element-215">Criteria Assessment:</h4>
              <div className="space-y-2" data-id="element-216">
                {evaluation.criteriaResults.map((result, index) => <div key={index} className="flex items-start" data-id="element-217">
                    <div className={`flex-shrink-0 mt-0.5 ${result.met ? 'text-green-500' : 'text-amber-500'}`} data-id="element-218">
                      {result.met ? <CheckCircle2 className="w-5 h-5" data-id="element-219" /> : <AlertTriangle className="w-5 h-5" data-id="element-220" />}
                    </div>
                    <div className="ml-2" data-id="element-221">
                      <p className="text-gray-800" data-id="element-222">{result.criterion}</p>
                      <p className="text-sm text-gray-600" data-id="element-223">{result.feedback}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="mb-6" data-id="element-224">
              <h4 className="font-medium mb-2" data-id="element-225">Overall Feedback:</h4>
              <p className="text-gray-700" data-id="element-226">{evaluation.overallFeedback}</p>
            </div>
            <div className="flex justify-end" data-id="element-227">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" onClick={completeChallenge} data-id="element-228">
                {status === 'success' ? 'Continue to Next Phase' : 'Accept Feedback & Continue'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}